import { useEffect, useMemo, useState } from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Notification from 'epn-ui/umd/dist/components/Notification';
import * as Sentry from '@sentry/nextjs';
import clsx from 'clsx';

import AddPlacementStep from '@components/pages/placements/add/AddPlacementStep';
import WithRole, { rolesEnum } from '@HOCs/WithRole';

import useI18n, { getLocalePaths } from '@i18n';
import api from '@api';
import apiHooks from '@apiHooks';
import { IGetUserProfileDataFormatted } from '@api/types';
import { AMPLITUDE_EVENTS_KEYS, logAmplitudeEvent } from 'src/utils/metrics/amplitude';
import sendGoal from '@utils/metrics/ymSendGoal';
import sendGaEvent from 'src/utils/metrics/ga';
import ConfirmationStep from '@components/pages/placements/add/ConfirmationStep';
import ReviewModal from '@components/pages/placements/add/ReviewModal';

import { ALI_ID, DEFAULT_VALUES } from '@components/pages/placements/add/constants';
import { IAddPlacementForm } from '@components/pages/placements/add/interfaces';

const { useUserProfile } = apiHooks;

export enum PageSteps {
    form = 'form',
    confirmation = 'confirmation',
    review = 'review',
}

const AddPlacement: React.FC = () => {
    const { t } = useI18n('Placements');
    const { t: tErrors } = useI18n('Errors');
    const [createInProgress, setCreateInProgress] = useState<boolean>(false);

    const { data: userData, isLoading } = useUserProfile<IGetUserProfileDataFormatted>();
    const [step, setStep] = useState<PageSteps>(PageSteps.form);
    const [formData, setFormData] = useState(DEFAULT_VALUES);
    const [showModal, setShowModal] = useState(false);

    const afterReg: boolean = useMemo(() => userData?.onboardAfterReg, [userData]);

    const handleErrors = async error => {
        if (error && error.response && typeof error.response.json === 'function') {
            const resp = await error.response.json();

            if (Array.isArray(resp.errors) && resp.errors.length !== 0) {
                resp.errors.forEach(item => {
                    if (item.error_description === 'placement is already exist') {
                        Notification.error({
                            message: t('Connected earlier'),
                        });
                    } else {
                        Notification.error({
                            message: item.error_description
                                ? tErrors(item.error_description)
                                : t('Something went wrong. Try again later'),
                        });
                    }
                });
            } else {
                Notification.error({ message: t('Something went wrong. Try again later') });
            }

            return;
        }

        Sentry.captureException(error, {
            extra: {
                ...formData,
            },
        });
    };

    const handleSuccessResult = (form: IAddPlacementForm): void => {
        setShowModal(true);
        setFormData(form);

        sendGoal('add_placement_new', 89120816);

        sendGaEvent({
            eventCategory: 'placements',
            eventLabel: 'add',
        });
    };

    const onSubmitHandler = async (form: IAddPlacementForm): Promise<void> => {
        try {
            setCreateInProgress(true);

            const create = await api.placement.addUserPlacement({
                body: {
                    url: form.url,
                    offerId: form.offer || ALI_ID,
                    categoryId: form.trafficSource,
                },
            });

            if (create.result) {
                handleSuccessResult(form);

                logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.addPlacement, {
                    offer_id: formData.offer,
                });
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setCreateInProgress(false);
        }
    };

    useEffect((): void => {
        if (userData && !afterReg) {
            api.user.updateProfile({
                body: {
                    onboardAfterReg: true,
                },
            });
        }
    }, [afterReg, userData]);

    return (
        <>
            <div className={clsx({ hidden: step !== PageSteps.form })}>
                <AddPlacementStep
                    afterReg={afterReg}
                    onSubmit={onSubmitHandler}
                    onNext={(data: IAddPlacementForm) => {
                        setStep(PageSteps.confirmation);
                        setFormData(data);
                    }}
                    createInProgress={createInProgress}
                    isLoading={isLoading}
                />
            </div>
            {step === PageSteps.confirmation && (
                <ConfirmationStep
                    createInProgress={createInProgress}
                    afterReg={afterReg}
                    formData={formData}
                    onSubmit={onSubmitHandler}
                    onBack={() => setStep(PageSteps.form)}
                />
            )}
            {showModal && <ReviewModal formData={formData} />}
        </>
    );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async () => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(AddPlacement, [rolesEnum.user]);
